import { UserOptions } from 'jspdf-autotable';
// Type-only: erased at compile time, so it does not defeat the lazy import in loadPdfLibs.
import type { jsPDF } from 'jspdf';
import Vue from 'vue';
import { Component } from 'vue-property-decorator';
import { NotificationOptions } from '@/util/NotificationOptionsPresets';

export interface ExportField {
    key: string;
    label?: string;
    formatter?: (value: any, key?: string, item?: any) => any;
}

// jspdf-autotable's own options, plus the heading to print when it should differ from the file
// name. The two are the same for most reports; Ordering Reports names its file by date, which
// is not what belongs at the top of the page.
export interface ExportPdfOptions extends UserOptions {
    title?: string;
}

const SEPARATOR = ',';
// Without it Excel on Windows reads the UTF-8 bytes as ANSI and mangles non-ASCII cells.
const BOM = '\uFEFF';

// Excel and Sheets evaluate any cell opening with one of these as a formula, and RFC 4180
// quoting does not stop them \u2014 quoting governs parsing, evaluation happens after. So a fax
// number stored as "+15551234567" opens as #NAME? instead of the number, and operator-entered
// text starting with "=" becomes a live formula in whoever's spreadsheet opens the file.
const FORMULA_TRIGGER = /^[=+\-@\t\r]/;

// A leading minus is the exception. The test is not "does this look like a number" but "does
// the spreadsheet's reading preserve the value": "-5" and "-$1,234.56" are read as the negative
// numbers they are, so marking them as text would silently cost every balance and quantity
// column its arithmetic. A leading "+" gets no such exemption \u2014 Excel reads "+15551234567" as
// 15551234567 and drops the sign, which is the fax-number case this guard exists for.
const NEGATIVE_NUMBER = /^-\$?(?:\d+|\d{1,3}(?:,\d{3})+)(?:\.\d+)?$/;

// One pass over the cell rather than a separate scan per character class. The ',' here must
// stay in step with SEPARATOR above.
const NEEDS_QUOTING = /["\r\n,]/;

// These reports run 8-12 columns. Portrait fits them only by breaking values mid-token \u2014
// a ten-digit fax number splits across two lines \u2014 so exports default to landscape.
const PDF_ORIENTATION = 'landscape' as const;
const TITLE_FONT_SIZE = 14;
// Matches jspdf-autotable's default left margin so the title lines up with the table edge.
const TITLE_X = 14;
const TITLE_Y = 15;
const TABLE_START_Y = 22;

@Component
export class ReportExporter extends Vue {

    public async ExportPDF(reportName: string, HtmlTable: any, options?: UserOptions): Promise<void> {
        try {
            const [jspdf, autotable] = await this.loadPdfLibs();

            const doc = this.newTitledDocument(jspdf, reportName);
            autotable.default(doc, {
                html: HtmlTable,
                includeHiddenHtml: false,
                showFoot: 'never',
                startY: TABLE_START_Y,
                ...options
            });
            doc.save(`${reportName}.pdf`);
        } catch (err) {
            this.notifyExportFailed(reportName, err);
        }
    }

    public ExportCSV(reportName: string, reportItems: any[], reportFields: ExportField[]): void {
        try {
            const { headers, rows } = this.buildExportGrid(reportItems, reportFields);
            const csvContent = [headers, ...rows]
                .map(row => row.map(cell => this.escapeCsvCell(cell)).join(SEPARATOR))
                .join('\n');

            this.downloadBlob(new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' }), `${reportName}.csv`);
        } catch (err) {
            this.notifyExportFailed(reportName, err);
        }
    }

    private downloadBlob(blob: Blob, fileName: string): void {
        if (navigator.msSaveBlob) { // IE 10+
            navigator.msSaveBlob(blob, fileName);
            return;
        }
        const link = document.createElement('a');
        if (link.download !== undefined) {
            // Browsers that support HTML5 download attribute
            const url = URL.createObjectURL(blob);
            link.setAttribute('href', url);
            link.setAttribute('download', fileName);
            link.style.visibility = 'hidden';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        }
    }

    public async ExportDataPDF(reportName: string, reportItems: any[], reportFields: ExportField[], options?: ExportPdfOptions): Promise<void> {
        try {
            const { headers, rows } = this.buildExportGrid(reportItems, reportFields);
            const [jspdf, autotable] = await this.loadPdfLibs();

            // Split off the heading so the rest still reaches autotable untouched.
            const { title, ...tableOptions } = options ?? {};
            const doc = this.newTitledDocument(jspdf, title ?? reportName);
            autotable.default(doc, {
                head: [headers],
                body: rows,
                showFoot: 'never',
                startY: TABLE_START_Y,
                ...tableOptions
            });
            doc.save(`${reportName}.pdf`);
        } catch (err) {
            this.notifyExportFailed(reportName, err);
        }
    }

    // A failed export must not be indistinguishable from a click that never registered. Callers
    // handle their own data-fetch failures; this covers everything from the grid build onward
    // (a throwing formatter, a jsPDF chunk that will not load) for all of them at once.
    // protected: a report that builds its own document rather than going through the helpers
    // above still needs to report a failure the same way, in the same words.
    protected notifyExportFailed(reportName: string, err: unknown): void {
        console.error(`Export failed: ${reportName}`, err);
        // $notification arrives from a global plugin, so it is absent when the mixin is used
        // outside a fully bootstrapped app. A handler that throws would turn a failure we
        // just caught back into an unhandled one, which is the behaviour this replaces.
        this.$notification?.(NotificationOptions.error(
            `Could not export ${reportName}. Please try again, and contact support if it keeps happening.`));
    }

    // The report name previously reached only the filename, so a printed or forwarded PDF
    // carried nothing identifying which report it was.
    private newTitledDocument(jspdf: typeof import("jspdf"), reportName: string): jsPDF {
        const doc = new jspdf.default({ orientation: PDF_ORIENTATION });
        doc.setFontSize(TITLE_FONT_SIZE);
        doc.text(reportName, TITLE_X, TITLE_Y);
        return doc;
    }

    // b-table field arrays double as export definitions, but they also carry render-only
    // columns (action buttons, expand toggles) and columns the table renders through a slot
    // rather than the field's own formatter. Pass those keys as overrides — `null` drops the
    // column, an object merges over it — so a report keeps one field list instead of two that
    // silently drift apart.
    protected ToExportFields(fields: any[], overrides: Record<string, Partial<ExportField> | null> = {}): ExportField[] {
        return fields.reduce((kept: ExportField[], field: any) => {
            const override = overrides[field.key];
            if (override !== null) {
                kept.push({ ...field, ...override });
            }
            return kept;
        }, []);
    }

    private loadPdfLibs(): Promise<[typeof import("jspdf"), typeof import("jspdf-autotable")]> {
        // Loaded together: the second import does not depend on the first, and awaiting them
        // in sequence costs an extra round-trip on the first export of a session.
        return Promise.all([
            import(/* webpackChunkName: "jspdf" */ "jspdf"),
            import(/* webpackChunkName: "jspdf" */ "jspdf-autotable")
        ]);
    }

    // Resolves and formats every cell once, up front, into the string grid both exporters want.
    // Formatters are invoked with (value, key, item) so a field list written for bootstrap-vue
    // exports exactly what the table renders.
    private buildExportGrid(reportItems: any[], reportFields: ExportField[]): { headers: string[]; rows: string[][] } {
        const headers = reportFields.map(f => f.label ?? this.humanizeKey(f.key));
        const rows = reportItems.map(item => reportFields.map(field => {
            const value = this.resolveCell(item, field.key);
            let cell: any;
            try {
                cell = field.formatter ? field.formatter(value, field.key, item) : value;
            } catch (err) {
                // Name the column. A bare "x.toFixed is not a function" out of a twelve-column
                // report tells whoever picks up the ticket nothing about where to look.
                const reason = err instanceof Error ? err.message : String(err);
                throw new Error(`Failed to format column "${field.label ?? field.key}": ${reason}`);
            }
            return cell === null || cell === undefined ? '' : String(cell);
        }));
        return { headers, rows };
    }

    // Missing values collapse to '' rather than undefined: formatters here are shared with
    // b-table, and several dereference the value (`value.lastName`), so a missing parent must
    // not hand them undefined.
    private resolveCell(item: any, key: string): any {
        let cell = item;
        key.split('.').forEach(k => {
            cell = cell === null || cell === undefined || cell[k] === null || cell[k] === undefined ? '' : cell[k];
        });
        return cell;
    }

    private escapeCsvCell(text: string): string {
        // A leading apostrophe is the standard mitigation: spreadsheet apps consume it as a
        // "treat the rest as text" marker rather than showing it, so the value reads as exported.
        const safe = FORMULA_TRIGGER.test(text) && !NEGATIVE_NUMBER.test(text) ? `'${text}` : text;
        if (NEEDS_QUOTING.test(safe)) {
            // Inner quotes must be doubled per RFC 4180, else the field splits into two columns.
            return `"${safe.replace(/"/g, '""')}"`;
        }
        return safe;
    }

    private humanizeKey(key: string): string {
        const last = key.split('.').pop() ?? key;
        return last.replace(/([a-z0-9])([A-Z])/g, '$1 $2').replace(/^./, c => c.toUpperCase());
    }
}
